import { mergeUserStats } from '@trackmind/core';
import { collectFinanceFiles, restoreFinanceFiles } from '../providers/finance-files';
// Tombstone cleanup: remove tombstones older than this many days
const TOMBSTONE_RETENTION_DAYS = 30;
/**
 * Entity types whose id is a natural key that gets reused: a habit's tick for
 * a day (`habit_id:date`), a day's weigh-in (`bw-YYYY-MM-DD`), a day's plan
 * (its date), the one settings row. Deleting one and saving it again is
 * ordinary — undo then redo — so for these a deletion only beats a row that
 * is older than it. Every other type has a fresh uuid per record, an id is
 * never reused, and "delete wins" stays exactly as it was.
 */
const REUSED_KEY_TYPES = new Set([
    'habit_instance', 'fitness_bodyweight', 'fitness_settings', 'schedule',
]);
/** Was this row written after `deletedAt`? A row with no timestamp never is. */
function newerThan(row, deletedAt) {
    const stamp = row?.updated_at ?? row?.created_at ?? row?.generated_at;
    if (!stamp)
        return false;
    const at = Date.parse(stamp);
    const del = Date.parse(deletedAt);
    return Number.isFinite(at) && Number.isFinite(del) && at > del;
}
/** Every row in an export, by entity type and the key sync identifies it by. */
function rowsByType(data) {
    const out = new Map();
    const add = (type, rows, key) => {
        const map = new Map();
        for (const row of rows ?? []) {
            const k = key(row);
            if (k)
                map.set(k, row);
        }
        out.set(type, map);
    };
    const byId = (row) => row.id;
    add('task', data.tasks, byId);
    add('project', data.projects, byId);
    add('schedule', data.schedules, (row) => row.date);
    add('goal', data.goals, byId);
    add('note', data.notes, byId);
    add('list', data.lists, byId);
    add('list_instance', data.listInstances, byId);
    add('habit_instance', data.habitInstances, (row) => `${row.habit_id}:${row.date}`);
    add('journal', data.journalEntries, byId);
    add('mood_checkin', data.moodCheckIns, byId);
    add('review', data.reviews, byId);
    add('fitness_workout', data.fitnessWorkouts, byId);
    add('fitness_bodyweight', data.fitnessBodyweights, byId);
    add('fitness_settings', data.fitnessSettings, byId);
    return out;
}
/**
 * Sync engine for coordinating data between local storage and GitHub
 */
// Minimum time between syncs to avoid redundant API calls (in milliseconds)
const MIN_SYNC_INTERVAL_MS = 90 * 1000; // 90 seconds
export class SyncEngine {
    storage;
    /**
     * The cloud storage backend, injected. The engine talks only to the
     * CloudStorageProvider interface — merge, tombstones, auto-sync, and raw
     * file access work identically on GitHub and Google Drive, and any new
     * provider gets all of it by implementing the interface.
     */
    provider;
    config;
    status = 'idle';
    syncTimer = null;
    listeners = new Set();
    lastSuccessfulSyncTime = 0; // Timestamp of last successful sync
    constructor(storage, provider, config) {
        this.storage = storage;
        this.provider = provider;
        this.config = {
            ...config,
            autoSync: config.autoSync ?? false,
            syncIntervalMs: config.syncIntervalMs ?? 2 * 60 * 1000, // 2 minutes default
        };
    }
    /**
     * Get current sync status
     */
    getStatus() {
        return this.status;
    }
    /**
     * Subscribe to status changes
     */
    onStatusChange(callback) {
        this.listeners.add(callback);
        return () => this.listeners.delete(callback);
    }
    setStatus(status) {
        this.status = status;
        this.listeners.forEach((cb) => cb(status));
    }
    /**
     * Start automatic syncing
     */
    startAutoSync() {
        if (this.syncTimer) {
            return;
        }
        this.syncTimer = setInterval(() => {
            // Skip if a sync happened recently (within MIN_SYNC_INTERVAL_MS)
            // This prevents redundant syncs when debounced/visibility syncs already fired
            const timeSinceLastSync = Date.now() - this.lastSuccessfulSyncTime;
            if (timeSinceLastSync < MIN_SYNC_INTERVAL_MS) {
                console.log(`[SyncEngine] Skipping interval sync - last sync was ${Math.round(timeSinceLastSync / 1000)}s ago`);
                return;
            }
            this.sync().catch(console.error);
        }, this.config.syncIntervalMs);
    }
    /**
     * Stop automatic syncing
     */
    stopAutoSync() {
        if (this.syncTimer) {
            clearInterval(this.syncTimer);
            this.syncTimer = null;
        }
    }
    /**
     * Sync if enough time has passed since last sync
     * Used by visibility change and debounced sync triggers
     * Returns true if sync was triggered, false if skipped
     */
    async syncIfNeeded() {
        const timeSinceLastSync = Date.now() - this.lastSuccessfulSyncTime;
        if (timeSinceLastSync < MIN_SYNC_INTERVAL_MS) {
            console.log(`[SyncEngine] Skipping sync - last sync was ${Math.round(timeSinceLastSync / 1000)}s ago`);
            return false;
        }
        await this.sync();
        return true;
    }
    /**
     * Get time since last successful sync in milliseconds
     */
    getTimeSinceLastSync() {
        return Date.now() - this.lastSuccessfulSyncTime;
    }
    /**
     * Initialize sync - ensure repository exists and is set up
     */
    async initialize() {
        await this.provider.initialize();
    }
    /**
     * Perform full sync
     */
    async sync(options = {}) {
        if (this.status === 'syncing') {
            return {
                success: false,
                status: 'syncing',
                message: 'Sync already in progress',
            };
        }
        const conflictStrategy = options.conflictStrategy ?? 'newest-wins';
        try {
            this.setStatus('syncing');
            // Check network
            if (!navigator.onLine) {
                this.setStatus('offline');
                return {
                    success: false,
                    status: 'offline',
                    message: 'No network connection',
                };
            }
            // Get local data and pending changes
            console.log('[SyncEngine] Getting local data...');
            const localData = await this.storage.exportAll();
            const pendingChanges = await this.storage.getPendingChanges();
            const lastSyncTime = await this.storage.getLastSyncTime();
            console.log('[SyncEngine] Local data:', { tasks: localData.tasks?.length, pending: pendingChanges.length });
            // Get remote data + metadata in parallel — they're independent calls
            // and serializing them just doubled the round-trip cost.
            console.log('[SyncEngine] Getting remote data...');
            const [remoteData, syncMetadata] = await Promise.all([
                this.provider.loadExportData(),
                this.provider.getSyncMetadata(),
            ]);
            console.log('[SyncEngine] Remote data:', remoteData ? 'exists' : 'null', 'metadata:', syncMetadata ? 'exists' : 'null');
            let result;
            if (options.forceDirection === 'push') {
                // Force push local to remote
                console.log('[SyncEngine] Force push requested');
                result = await this.forcePush(localData, pendingChanges);
            }
            else if (options.forceDirection === 'pull') {
                // Force pull remote to local
                console.log('[SyncEngine] Force pull requested');
                result = await this.forcePull(remoteData);
            }
            else if (!remoteData) {
                // No remote data - initial push
                console.log('[SyncEngine] No remote data, doing initial push');
                result = await this.initialPush(localData);
            }
            else if (pendingChanges.length === 0 && !syncMetadata) {
                // No local changes and no sync history - pull remote
                console.log('[SyncEngine] No local changes and no sync history, pulling remote');
                result = await this.forcePull(remoteData);
            }
            else {
                // Normal sync - merge changes
                console.log('[SyncEngine] Merge sync');
                result = await this.mergeSync(localData, remoteData, pendingChanges, lastSyncTime, conflictStrategy, options.dryRun ?? false);
            }
            console.log('[SyncEngine] Result:', result);
            if (result.success) {
                await this.storage.setLastSyncTime(new Date());
                this.lastSuccessfulSyncTime = Date.now(); // Track for skip-if-recent logic
                this.setStatus('idle');
            }
            else if (result.conflicts && result.conflicts.length > 0) {
                this.setStatus('conflict');
            }
            else {
                this.setStatus('error');
            }
            return result;
        }
        catch (error) {
            console.error('[SyncEngine] Sync error:', error);
            this.setStatus('error');
            return {
                success: false,
                status: 'error',
                message: error instanceof Error ? error.message : 'Unknown error during sync',
            };
        }
    }
    /**
     * Force push local data to remote
     */
    async forcePush(localData, pendingChanges) {
        const syncRef = await this.provider.saveExportData(localData, 'Force push from device');
        await this.provider.saveSyncMetadata({
            lastSyncRef: syncRef,
            lastSyncTime: new Date().toISOString(),
            deviceId: this.config.deviceId,
        });
        // Clear pending changes
        await this.storage.clearPendingChanges(pendingChanges.map((c) => c.id));
        return {
            success: true,
            status: 'idle',
            message: 'Force pushed local data to remote',
            syncedAt: new Date(),
            changesPushed: pendingChanges.length,
            changesPulled: 0,
        };
    }
    /**
     * Force pull remote data to local
     */
    async forcePull(remoteData) {
        if (!remoteData) {
            return {
                success: false,
                status: 'error',
                message: 'No remote data to pull',
            };
        }
        await this.storage.importAll(remoteData);
        const remoteRef = await this.provider.getRemoteRef();
        if (remoteRef) {
            await this.provider.saveSyncMetadata({
                lastSyncRef: remoteRef,
                lastSyncTime: new Date().toISOString(),
                deviceId: this.config.deviceId,
            });
        }
        // Clear any pending changes since we've overwritten local
        const pendingChanges = await this.storage.getPendingChanges();
        await this.storage.clearPendingChanges(pendingChanges.map((c) => c.id));
        return {
            success: true,
            status: 'idle',
            message: 'Force pulled remote data to local',
            syncedAt: new Date(),
            changesPushed: 0,
            changesPulled: 1,
        };
    }
    /**
     * Initial push when remote is empty
     */
    async initialPush(localData) {
        const syncRef = await this.provider.saveExportData(localData, 'Initial sync');
        await this.provider.saveSyncMetadata({
            lastSyncRef: syncRef,
            lastSyncTime: new Date().toISOString(),
            deviceId: this.config.deviceId,
        });
        // Clear pending changes
        const pendingChanges = await this.storage.getPendingChanges();
        await this.storage.clearPendingChanges(pendingChanges.map((c) => c.id));
        return {
            success: true,
            status: 'idle',
            message: 'Initial data pushed to remote',
            syncedAt: new Date(),
            changesPushed: 1,
            changesPulled: 0,
        };
    }
    /**
     * Merge sync - handle both local and remote changes
     */
    async mergeSync(localData, remoteData, pendingChanges, _lastSyncTime, conflictStrategy, dryRun) {
        const conflicts = [];
        const mergedData = { ...localData };
        let changesPulled = 0;
        // Build sets of locally deleted entity IDs from pending changes
        const locallyDeletedIds = new Map();
        for (const change of pendingChanges) {
            if (change.operation === 'delete') {
                const entityType = change.type;
                if (!locallyDeletedIds.has(entityType)) {
                    locallyDeletedIds.set(entityType, new Set());
                }
                locallyDeletedIds.get(entityType).add(change.entityId);
            }
        }
        // A pending delete for a row that is back in local storage was undone
        // here afterwards — the row can only exist again because it was saved
        // again. Local storage is this device's latest word, so the delete no
        // longer applies. (Tick a habit, untick, tick: the second tick used to
        // be dropped by this sync and then by every device.)
        const localRows = rowsByType(localData);
        for (const [entityType, ids] of locallyDeletedIds) {
            const present = localRows.get(entityType);
            if (present)
                for (const id of present.keys())
                    ids.delete(id);
        }
        // Build set of remotely deleted IDs from remote tombstones
        const remotelyDeletedIds = new Map();
        const remoteTombstones = remoteData.tombstones?.tombstones || [];
        const remoteRows = rowsByType(remoteData);
        for (const tombstone of remoteTombstones) {
            const entityType = tombstone.entityType;
            // For a key that is reused by design — one habit tick per day, one
            // weigh-in per day, one plan per day — a row saved AFTER the deletion
            // is a new record under the same key, not the deleted one coming back.
            if (REUSED_KEY_TYPES.has(entityType) && (newerThan(localRows.get(entityType)?.get(tombstone.entityId), tombstone.deletedAt) ||
                newerThan(remoteRows.get(entityType)?.get(tombstone.entityId), tombstone.deletedAt)))
                continue;
            if (!remotelyDeletedIds.has(entityType)) {
                remotelyDeletedIds.set(entityType, new Set());
            }
            remotelyDeletedIds.get(entityType).add(tombstone.entityId);
        }
        // Merge each entity type
        mergedData.tasks = this.mergeEntities(localData.tasks, remoteData.tasks, pendingChanges.filter((c) => c.type === 'task'), conflictStrategy, 'task', conflicts, 'id', locallyDeletedIds.get('task') || new Set(), remotelyDeletedIds.get('task') || new Set());
        changesPulled += this.countNewFromRemote(localData.tasks, remoteData.tasks);
        mergedData.projects = this.mergeEntities(localData.projects, remoteData.projects, pendingChanges.filter((c) => c.type === 'project'), conflictStrategy, 'project', conflicts, 'id', locallyDeletedIds.get('project') || new Set(), remotelyDeletedIds.get('project') || new Set());
        changesPulled += this.countNewFromRemote(localData.projects, remoteData.projects);
        mergedData.schedules = this.mergeEntities(localData.schedules, remoteData.schedules, pendingChanges.filter((c) => c.type === 'schedule'), conflictStrategy, 'schedule', conflicts, 'date', locallyDeletedIds.get('schedule') || new Set(), remotelyDeletedIds.get('schedule') || new Set());
        changesPulled += this.countNewFromRemote(localData.schedules, remoteData.schedules, 'date');
        mergedData.goals = this.mergeEntities(localData.goals, remoteData.goals, pendingChanges.filter((c) => c.type === 'goal'), conflictStrategy, 'goal', conflicts, 'id', locallyDeletedIds.get('goal') || new Set(), remotelyDeletedIds.get('goal') || new Set());
        changesPulled += this.countNewFromRemote(localData.goals, remoteData.goals);
        mergedData.notes = this.mergeEntities(localData.notes, remoteData.notes, pendingChanges.filter((c) => c.type === 'note'), conflictStrategy, 'note', conflicts, 'id', locallyDeletedIds.get('note') || new Set(), remotelyDeletedIds.get('note') || new Set());
        changesPulled += this.countNewFromRemote(localData.notes, remoteData.notes);
        mergedData.lists = this.mergeEntities(localData.lists, remoteData.lists, pendingChanges.filter((c) => c.type === 'list'), conflictStrategy, 'list', conflicts, 'id', locallyDeletedIds.get('list') || new Set(), remotelyDeletedIds.get('list') || new Set());
        changesPulled += this.countNewFromRemote(localData.lists, remoteData.lists);
        mergedData.listInstances = this.mergeEntities(localData.listInstances, remoteData.listInstances, pendingChanges.filter((c) => c.type === 'list_instance'), conflictStrategy, 'list_instance', conflicts, 'id', locallyDeletedIds.get('list_instance') || new Set(), remotelyDeletedIds.get('list_instance') || new Set());
        changesPulled += this.countNewFromRemote(localData.listInstances, remoteData.listInstances);
        // HabitInstance uses composite key (habit_id:date), not 'id' field
        // Pre-process to add synthetic id, merge, then strip it
        const localHabitsWithId = (localData.habitInstances || []).map(hi => ({
            ...hi,
            id: `${hi.habit_id}:${hi.date}`,
        }));
        const remoteHabitsWithId = (remoteData.habitInstances || []).map(hi => ({
            ...hi,
            id: `${hi.habit_id}:${hi.date}`,
        }));
        const mergedHabitsWithId = this.mergeEntities(localHabitsWithId, remoteHabitsWithId, pendingChanges.filter((c) => c.type === 'habit_instance'), conflictStrategy, 'habit_instance', conflicts, 'id', locallyDeletedIds.get('habit_instance') || new Set(), remotelyDeletedIds.get('habit_instance') || new Set());
        // Strip the synthetic id field
        mergedData.habitInstances = mergedHabitsWithId.map(({ id, ...rest }) => rest);
        changesPulled += this.countNewFromRemote(localHabitsWithId, remoteHabitsWithId);
        mergedData.journalEntries = this.mergeEntities(localData.journalEntries || [], remoteData.journalEntries || [], pendingChanges.filter((c) => c.type === 'journal'), conflictStrategy, 'journal', conflicts, 'id', locallyDeletedIds.get('journal') || new Set(), remotelyDeletedIds.get('journal') || new Set());
        changesPulled += this.countNewFromRemote(localData.journalEntries || [], remoteData.journalEntries || []);
        // Merge mood check-ins
        mergedData.moodCheckIns = this.mergeEntities(localData.moodCheckIns || [], remoteData.moodCheckIns || [], pendingChanges.filter((c) => c.type === 'mood_checkin'), conflictStrategy, 'mood_checkin', conflicts, 'id', locallyDeletedIds.get('mood_checkin') || new Set(), remotelyDeletedIds.get('mood_checkin') || new Set());
        changesPulled += this.countNewFromRemote(localData.moodCheckIns || [], remoteData.moodCheckIns || []);
        // Merge reviews. Last-write-wins on `updated_at`, which moves when the
        // notes change — the generated content is derivable, the notes are not,
        // so the newer text is what has to survive two devices editing the same
        // period's review.
        mergedData.reviews = this.mergeEntities(localData.reviews || [], remoteData.reviews || [], pendingChanges.filter((c) => c.type === 'review'), conflictStrategy, 'review', conflicts, 'id', locallyDeletedIds.get('review') || new Set(), remotelyDeletedIds.get('review') || new Set());
        changesPulled += this.countNewFromRemote(localData.reviews || [], remoteData.reviews || []);
        // Fitness: the same id-keyed merge as everything else. A workout's sets
        // are edited in place while it is in progress, so `updated_at` is what
        // resolves a session touched on two devices; bodyweights have one id per
        // DAY, so two devices weighing in the same morning resolve to one row;
        // settings is a one-row list for the same reason.
        mergedData.fitnessWorkouts = this.mergeEntities(localData.fitnessWorkouts || [], remoteData.fitnessWorkouts || [], pendingChanges.filter((c) => c.type === 'fitness_workout'), conflictStrategy, 'fitness_workout', conflicts, 'id', locallyDeletedIds.get('fitness_workout') || new Set(), remotelyDeletedIds.get('fitness_workout') || new Set());
        changesPulled += this.countNewFromRemote(localData.fitnessWorkouts || [], remoteData.fitnessWorkouts || []);
        mergedData.fitnessBodyweights = this.mergeEntities(localData.fitnessBodyweights || [], remoteData.fitnessBodyweights || [], pendingChanges.filter((c) => c.type === 'fitness_bodyweight'), conflictStrategy, 'fitness_bodyweight', conflicts, 'id', locallyDeletedIds.get('fitness_bodyweight') || new Set(), remotelyDeletedIds.get('fitness_bodyweight') || new Set());
        changesPulled += this.countNewFromRemote(localData.fitnessBodyweights || [], remoteData.fitnessBodyweights || []);
        mergedData.fitnessSettings = this.mergeEntities(localData.fitnessSettings || [], remoteData.fitnessSettings || [], pendingChanges.filter((c) => c.type === 'fitness_settings'), conflictStrategy, 'fitness_settings', conflicts, 'id', locallyDeletedIds.get('fitness_settings') || new Set(), remotelyDeletedIds.get('fitness_settings') || new Set());
        changesPulled += this.countNewFromRemote(localData.fitnessSettings || [], remoteData.fitnessSettings || []);
        // Merge tombstones from local and remote
        const localTombstones = localData.tombstones?.tombstones || [];
        // A tombstone superseded by a newer row under the same key has done its
        // job; carrying it on would delete that row on a device whose copy is
        // missing a timestamp.
        const mergedRows = rowsByType(mergedData);
        const mergedTombstones = this.mergeTombstones(localTombstones, remoteTombstones).filter((t) => !(REUSED_KEY_TYPES.has(t.entityType) &&
            newerThan(mergedRows.get(t.entityType)?.get(t.entityId), t.deletedAt)));
        mergedData.tombstones = {
            tombstones: mergedTombstones,
            cleanedUpBefore: localData.tombstones?.cleanedUpBefore,
        };
        // Merge stats and preferences (singletons — no id field, just whole-
        // object compare by updated_at). Without this step the initial
        // `{ ...localData }` spread above silently kept local's copy on every
        // sync, so whichever device synced last wrote its stats/preferences
        // to the remote and clobbered the other side. Both fields acquired
        // an optional `updated_at` for this purpose; legacy data without one
        // is treated as epoch zero, so the side that has been stamped wins.
        const pickNewerSingleton = (local, remote) => {
            if (!local)
                return remote;
            if (!remote)
                return local;
            const lt = local.updated_at ? new Date(local.updated_at).getTime() : 0;
            const rt = remote.updated_at ? new Date(remote.updated_at).getTime() : 0;
            return lt >= rt ? local : remote;
        };
        // Stats merge field by field, not newest-wins: when both devices completed
        // work offline, the whole-object pick dropped one device's XP, counts,
        // new badges and record streak. Totals and records take the larger,
        // badges are a union (core's mergeUserStats); the app then settles them
        // against the merged history, which restores anything both sides added.
        mergedData.stats = localData.stats && remoteData.stats
            ? mergeUserStats(localData.stats, remoteData.stats)
            : (localData.stats ?? remoteData.stats);
        mergedData.preferences = pickNewerSingleton(localData.preferences, remoteData.preferences);
        // One-line per-sync trace so future weirdness leaves a record without
        // having to rebuild the scenario from scratch.
        console.log('[SyncEngine] mergeSync result:', {
            tasks: { local: localData.tasks.length, remote: remoteData.tasks.length, merged: mergedData.tasks.length },
            tombstones: { local: localTombstones.length, remote: remoteTombstones.length, merged: mergedTombstones.length },
            stats: {
                localXp: localData.stats?.total_xp, remoteXp: remoteData.stats?.total_xp, mergedXp: mergedData.stats?.total_xp,
                localTs: localData.stats?.updated_at, remoteTs: remoteData.stats?.updated_at,
            },
            pendingChanges: pendingChanges.length,
            conflicts: conflicts.length,
        });
        // Handle manual conflicts
        if (conflictStrategy === 'manual' && conflicts.length > 0) {
            return {
                success: false,
                status: 'conflict',
                message: `${conflicts.length} conflict(s) require manual resolution`,
                conflicts,
            };
        }
        if (dryRun) {
            return {
                success: true,
                status: 'idle',
                message: `Dry run: would sync ${pendingChanges.length} changes and pull ${changesPulled} changes`,
                conflicts,
                changesPushed: pendingChanges.length,
                changesPulled,
            };
        }
        // Apply merged data locally
        await this.storage.importAll(mergedData);
        // Cleanup old tombstones (both locally and in merged data)
        const cleanedCount = await this.storage.cleanupTombstones(TOMBSTONE_RETENTION_DAYS);
        if (cleanedCount > 0) {
            console.log(`[SyncEngine] Cleaned up ${cleanedCount} old tombstones`);
            // Re-export to get cleaned tombstones for remote
            const cleanedData = await this.storage.exportAll();
            mergedData.tombstones = cleanedData.tombstones;
        }
        // Push merged data to remote
        const syncRef = await this.provider.saveExportData(mergedData, `Sync from ${this.config.deviceId}`);
        await this.provider.saveSyncMetadata({
            lastSyncRef: syncRef,
            lastSyncTime: new Date().toISOString(),
            deviceId: this.config.deviceId,
        });
        // Providers without built-in history (Drive) keep a periodic snapshot;
        // GitHub omits this — commit history already is the backup.
        try {
            await this.provider.createBackupIfNeeded?.(mergedData);
        }
        catch (backupError) {
            // A failed backup must never fail the sync itself
            console.warn('[SyncEngine] Backup skipped:', backupError);
        }
        // Clear pending changes
        await this.storage.clearPendingChanges(pendingChanges.map((c) => c.id));
        return {
            success: true,
            status: 'idle',
            message: conflicts.length > 0
                ? `Synced with ${conflicts.length} auto-resolved conflict(s)`
                : 'Sync completed successfully',
            conflicts: conflicts.length > 0 ? conflicts : undefined,
            syncedAt: new Date(),
            changesPushed: pendingChanges.length,
            changesPulled,
        };
    }
    /**
     * Merge tombstones from local and remote, removing duplicates
     */
    mergeTombstones(local, remote) {
        const merged = new Map();
        // Add all local tombstones
        for (const t of local) {
            const key = `${t.entityType}:${t.entityId}`;
            merged.set(key, t);
        }
        // Add remote tombstones (keep the newer one if duplicate)
        for (const t of remote) {
            const key = `${t.entityType}:${t.entityId}`;
            const existing = merged.get(key);
            if (!existing || t.deletedAt > existing.deletedAt) {
                merged.set(key, t);
            }
        }
        return Array.from(merged.values());
    }
    /**
     * Merge a list of entities with conflict resolution
     *
     * Key deletion handling:
     * - locallyDeletedIds: IDs that were deleted locally (pending delete operations)
     * - remotelyDeletedIds: IDs that were deleted remotely (from remote tombstones)
     *
     * Rules:
     * 1. If locally deleted → don't include in result (even if remote has it)
     * 2. If remotely deleted → don't include in result (even if local has it)
     * 3. Deletions always win over updates (delete is a terminal operation)
     */
    mergeEntities(local, remote, changes, strategy, entityType, conflicts, idField = 'id', locallyDeletedIds = new Set(), remotelyDeletedIds = new Set()) {
        const localMap = new Map();
        const remoteMap = new Map();
        const changedIds = new Set(changes.filter(c => c.operation !== 'delete').map((c) => c.entityId));
        local.forEach((item) => {
            const key = idField === 'id' ? item.id : item.date;
            if (key)
                localMap.set(key, item);
        });
        remote.forEach((item) => {
            const key = idField === 'id' ? item.id : item.date;
            if (key)
                remoteMap.set(key, item);
        });
        const result = new Map();
        // Process all local items
        for (const [id, localItem] of localMap) {
            // Skip if locally deleted (delete wins)
            if (locallyDeletedIds.has(id)) {
                continue;
            }
            // Skip if remotely deleted (delete wins)
            if (remotelyDeletedIds.has(id)) {
                continue;
            }
            const remoteItem = remoteMap.get(id);
            if (!remoteItem) {
                // Only in local - this is a new local item, keep it
                // (Remote doesn't have it and it's not in remote tombstones)
                result.set(id, localItem);
            }
            else if (changedIds.has(id)) {
                // Conflict - local was changed (not deleted) and remote exists
                const resolved = this.resolveConflict(localItem, remoteItem, strategy);
                conflicts.push({
                    entityType,
                    entityId: id,
                    localVersion: localItem,
                    remoteVersion: remoteItem,
                    resolvedWith: resolved === localItem ? 'local' : 'remote',
                });
                result.set(id, resolved);
            }
            else {
                // No local changes - use remote version
                result.set(id, remoteItem);
            }
        }
        // Add remote-only items (not locally deleted)
        for (const [id, remoteItem] of remoteMap) {
            // Skip if already processed from local
            if (localMap.has(id)) {
                continue;
            }
            // Skip if locally deleted (local delete wins)
            if (locallyDeletedIds.has(id)) {
                continue;
            }
            // Skip if remotely deleted (shouldn't happen but be safe)
            if (remotelyDeletedIds.has(id)) {
                continue;
            }
            // This is a new remote item, add it
            result.set(id, remoteItem);
        }
        return Array.from(result.values());
    }
    /**
     * Resolve a conflict between local and remote versions
     */
    resolveConflict(local, remote, strategy) {
        switch (strategy) {
            case 'local-wins':
                return local;
            case 'remote-wins':
                return remote;
            case 'newest-wins': {
                const localTime = local.updated_at ? new Date(local.updated_at).getTime() : 0;
                const remoteTime = remote.updated_at ? new Date(remote.updated_at).getTime() : 0;
                return localTime >= remoteTime ? local : remote;
            }
            case 'manual':
                // Return local for now - caller will handle
                return local;
            default:
                return local;
        }
    }
    /**
     * Count items that exist in remote but not in local
     */
    countNewFromRemote(local, remote, idField = 'id') {
        const localIds = new Set(local.map((item) => (idField === 'id' ? item.id : item.date)));
        return remote.filter((item) => !localIds.has(idField === 'id' ? item.id : item.date)).length;
    }
    /**
     * Check if there are pending changes to sync
     */
    async hasPendingChanges() {
        const changes = await this.storage.getPendingChanges();
        return changes.length > 0;
    }
    /**
     * Get count of pending changes
     */
    async getPendingChangeCount() {
        const changes = await this.storage.getPendingChanges();
        return changes.length;
    }
    /**
     * Check for remote changes without syncing
     */
    async checkForRemoteChanges() {
        const syncMetadata = await this.provider.getSyncMetadata();
        if (!syncMetadata || !syncMetadata.lastSyncRef) {
            // No previous sync - check if remote has data
            const remoteData = await this.provider.loadExportData();
            return remoteData !== null;
        }
        return this.provider.hasRemoteChanges(syncMetadata.lastSyncRef);
    }
    /** Which backend this engine is currently syncing to. */
    getProviderType() {
        return this.provider.providerType;
    }
    /**
     * Collect this user's finance files (finance-settings/investments/income
     * plus one per year) as a { filename: content } map. Finance lives in raw
     * files outside the synced database, so exports and provider switches must
     * carry it explicitly.
     */
    async collectFinanceFiles() {
        return collectFinanceFiles(this.provider);
    }
    /** Write a finance file map into the current provider. */
    async restoreFinanceFiles(files) {
        return restoreFinanceFiles(this.provider, files);
    }
    /**
     * Move this user's data to a different cloud provider.
     *
     * Local storage is the source of truth (local-first), so the database is
     * simply pushed to the new backend. The finance raw files are the part
     * that does NOT live locally — they're read from the OLD provider before
     * the switch and written to the new one, which is exactly what a manual
     * logout/login/sync loses.
     *
     * The old provider's copy is left untouched as a fallback.
     */
    async switchProvider(next, options = {}) {
        // 1. Read finance files from the CURRENT provider while we still have it
        let financeFiles = {};
        try {
            financeFiles = await collectFinanceFiles(this.provider);
        }
        catch (error) {
            console.warn('[SyncEngine] Could not read finance files from old provider:', error);
        }
        // 2. Point at the new backend and make sure its container exists
        this.provider = next;
        await next.initialize();
        // 3. Push the full local database
        const localData = await this.storage.exportAll();
        const syncRef = await next.saveExportData(localData, 'Migrated from another provider');
        await next.saveSyncMetadata({
            lastSyncRef: syncRef,
            lastSyncTime: new Date().toISOString(),
            deviceId: options.deviceId ?? this.config.deviceId,
        });
        // 4. Carry the finance files across
        const financeFilesMoved = Object.keys(financeFiles).length > 0
            ? await restoreFinanceFiles(next, financeFiles, 'finance: migrated from another provider')
            : 0;
        await this.storage.setLastSyncTime(new Date());
        const pending = await this.storage.getPendingChanges();
        await this.storage.clearPendingChanges(pending.map((c) => c.id));
        return { financeFilesMoved, syncRef };
    }
    /**
     * Read a raw file from the provider by path (finance-YYYY.json etc.).
     * Provider-agnostic: works on GitHub and Google Drive alike, and the
     * providers handle their own encoding (fixes the old bare-atob UTF-8 bug).
     */
    async readRawFile(path) {
        return this.provider.readRawFile(path);
    }
    /**
     * Write a raw file to the provider at the given path.
     */
    async writeRawFile(path, content, message) {
        return this.provider.writeRawFile(path, content, message);
    }
}
//# sourceMappingURL=sync-engine.js.map